import { ArrowDownRight, ArrowUpRight, EqualIcon, InfoIcon } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PropsWithChildren, ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface RiskHighlightCardProps {
  title: string;
  change?: number;
  isChangeGood?: boolean;
  changeDescription?: string;
  headline: string;
  text?: string;
  body?: ReactNode;
  info?: string;
  onClick?: React.ComponentProps<typeof Card>["onClick"];
}

export function RiskHighlightCard({
  title,
  change,
  isChangeGood,
  changeDescription,
  headline,
  text,
  body,
  info,
  onClick,
}: RiskHighlightCardProps) {
  const changeAbs = change !== undefined ? Math.abs(change) : undefined;
  const isChangePositive = change !== undefined ? change > 0 : undefined;
  const IconArrow = changeAbs === 0
    ? EqualIcon
    : isChangePositive
      ? ArrowUpRight
      : ArrowDownRight;

  const badgeVariant = isChangeGood
    ? "paid"
    : "rejected";

  return (
    <Card
      className={cn(
        "p-0 shadow-sm bg-gradient-to-br from-[#9F86C0]/15 to-white",
        onClick && "cursor-pointer",
      )}
      onClick={onClick}
    >
      <CardHeader className="p-5 relative">
        <div className="flex flex-row items-start justify-between">
          <p className="text-xs text-gray-500 uppercase">
            {title}
          </p>

          <div className="absolute right-4 top-4">
            <div className="flex flex-row items-start justify-end gap-1.5">
              {info && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-500 hover:bg-white/70 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5E548E]/40"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      aria-label={`${title} info`}
                    >
                      <InfoIcon className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    align="end"
                    className="max-w-xs text-xs leading-5"
                  >
                  {info}
                </TooltipContent>
              </Tooltip>
              )}
              {(change !== undefined && changeAbs !== undefined) || changeDescription ? (
                <div className="flex flex-col items-end gap-y-1">
                  {change !== undefined && changeAbs !== undefined && (
                    <Badge variant={badgeVariant}>
                      <div className="flex flex-row justify-center items-center gap-x-1">
                        <IconArrow className="size-3" />
                        <span>
                          {changeAbs.toFixed(1)} %
                        </span>
                      </div>
                    </Badge>
                  )}
                  {changeDescription && (
                    <p className="text-xs text-gray-500">
                      {changeDescription}
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          </div>

        </div>
      </CardHeader>
      <CardContent className="p-5 pt-0">
        <CardTitle className="text-[32px]">
          {headline}
        </CardTitle>
        {text && (
          <CardDescription className="mt-4 text-sm">
            {text}
          </CardDescription>
        )}
        {body && (
          <div className="mt-2">
            {body}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface RiskHighlightBadgeProps {
  dotColor?: string;
  text: string;
  onClick?: React.ComponentProps<typeof Badge>["onClick"];
}

export function RiskHighlightBadge({ dotColor, text, onClick }: RiskHighlightBadgeProps) {
  return (
    <Badge
      variant="outline"
      className="bg-white"
      onClick={onClick}
    >
      <div className="flex flex-row gap-x-1.5 items-center">
        {dotColor && (
          <div
            className="h-2 w-2 rounded-full bg-green-500"
            style={{ backgroundColor: dotColor }}
          />
        )}
        <p className="text-xs font-medium max-w-32 truncate">
          {text}
        </p>
      </div>
    </Badge>
  );
}

export function RiskHighlightBadges({ children }: PropsWithChildren) {
  return (
    <div className="flex flex-row flex-wrap gap-2">
      {children}
    </div>
  );
}
